import { useEffect } from 'react';

import {
  PartnerPropertyMap,
  resolvePartnerParamsToResolvedUrl,
} from '@/lib/auth/resolve-partner-params';

import { useAuth } from './use-auth';

/**
 * Resolves partner_id + partner_property_id query params to internal property selection params
 * using the /me session mapping. Runs synchronously before router creation so
 * the router never sees the unresolved URL (no loading screen or flash).
 *
 * Note: This effect only runs when the user session (and its mapping) is available,
 * so it effectively runs on initial load. In-app navigation to a URL that
 * contains partner_id + partner_property_id will not be resolved until the next
 * full load; partner links used inside the app should use already-resolved
 * `property_id` params when possible.
 */
export function useResolvePartnerParams(): void {
  const { user } = useAuth();

  useEffect(() => {
    if (
      !user?.business_partner_property_to_surface_property_map ||
      Object.keys(user.business_partner_property_to_surface_property_map)
        .length === 0
    ) {
      return;
    }

    const resolvedUrl = resolvePartnerParamsToResolvedUrl(
      {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
      },
      user.business_partner_property_to_surface_property_map as PartnerPropertyMap
    );
    if (resolvedUrl) {
      window.history.replaceState(null, '', resolvedUrl);
    }
  }, [user?.business_partner_property_to_surface_property_map]);
}
